/**
 * GA4 event helpers.
 *
 * Events are pushed to the GTM dataLayer, never to gtag() - GA is configured inside the
 * GTM container. The array is created by templates/includes/ga.html.twig, but it is
 * absent in editmode and in debug, so track() creates it defensively rather than throwing.
 */

/**
 * @param {string} name GA4 event name
 * @param {object} params
 */
function track(name, params) {
    window.dataLayer = window.dataLayer || [];

    // Null the previous payload first, otherwise GTM merges two events' items together.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: name, ecommerce: params });
}

/**
 * Reads the JSON payload an element carries in data-ga-item.
 * @param {Element} element
 * @returns {object|null}
 */
function readItem(element) {
    const raw = element?.dataset?.gaItem;

    if (!raw) {
        return null;
    }

    try {
        return JSON.parse(raw);
    } catch (error) {
        return null;
    }
}

/**
 * Sums price * quantity over items that carry a price.
 * @param {object[]} items
 */
function sumValue(items) {
    return (
        Math.round(
            items.reduce(
                (total, item) =>
                    total + (item.price ?? 0) * (item.quantity ?? 1),
                0,
            ) * 100,
        ) / 100
    );
}

/**
 * @param {object[]} items
 */
function currencyOf(items) {
    return items.find((item) => item.currency)?.currency;
}

/**
 * One view_item_list per distinct list on the page.
 */
function trackItemLists() {
    /** @type {Map<string, object[]>} */
    const lists = new Map();

    for (const element of document.querySelectorAll("[data-ga-item][data-ga-list]")) {
        const item = readItem(element);

        if (!item) {
            continue;
        }

        const listName = element.dataset.gaList;
        lists.has(listName) || lists.set(listName, []);
        lists.get(listName).push({ ...item, item_list_name: listName });
    }

    for (const [listName, items] of lists) {
        const currency = currencyOf(items);

        track("view_item_list", {
            item_list_name: listName,
            ...(currency ? { currency } : {}),
            items,
        });
    }
}

/**
 * select_item when a product card link is followed.
 */
function trackItemSelection() {
    document.addEventListener("click", (event) => {
        const container = event.target.closest("[data-ga-item][data-ga-list]");

        if (!container || !event.target.closest("a[href]")) {
            return;
        }

        const item = readItem(container);

        if (!item) {
            return;
        }

        track("select_item", {
            item_list_name: container.dataset.gaList,
            ...(item.currency ? { currency: item.currency } : {}),
            items: [item],
        });
    });
}

/**
 * Fires an event built from a single item, e.g. add_to_cart / remove_from_cart.
 * @param {string} name
 * @param {object|null} item
 * @param {number} quantity
 */
function trackItemEvent(name, item, quantity = 1) {
    if (!item) {
        return;
    }

    const payload = { ...item, quantity };

    // Only claim a value when the item is actually priced - anonymous visitors have none.
    track(
        name,
        payload.price === undefined
            ? { items: [payload] }
            : {
                  currency: payload.currency,
                  value: sumValue([payload]),
                  items: [payload],
              },
    );
}

/**
 * Page-level payload rendered server side into #ga-page-payload, used by view_cart
 * and begin_checkout where the whole cart is needed rather than a single item.
 * @returns {object|null}
 */
function readPagePayload() {
    const element = document.getElementById("ga-page-payload");

    if (!element) {
        return null;
    }

    try {
        return JSON.parse(element.textContent);
    } catch (error) {
        return null;
    }
}

function initializeTrackingModule() {
    trackItemLists();
    trackItemSelection();
}

export {
    initializeTrackingModule,
    track,
    trackItemEvent,
    readItem,
    readPagePayload,
};
